import React from "react";
import { useHistory } from "react-router-dom";

import {
  Box,
  Typography,
  Stack,
  Grid,
} from "@mui/material";

import {
  AutoAwesome as SparkleIcon,
  Bolt as InstantIcon,
  CheckCircle as VerifiedIcon,
  AddCircleOutline as AddIcon,
} from "@mui/icons-material";

import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonChip, CommonButton } from "../../../../Components/ui";
import { Path } from "../../../../../static/Constants";

const FiveSlotHeroBanner = ({
  totalActive = 0,
  combinedPrize = "0.00",
  currency = "BET",
  title = "5-SlotBET",
  description = "Spin 5 reels, match high-tier symbols from left to right, and hit the Mini, Major, or Mega progressive jackpot with on-chain transparency.",
}) => {
  const history = useHistory();

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        className="slot-hero-banner five-slot-hero-banner"
        sx={{ p: { xs: 3, md: 4 } }}
      >
        <Grid
          container
          spacing={3}
          alignItems="center"
        >
          <Grid item xs={12} md={7}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 52,
                  height: 44,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[200]}`,
                  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    position: "absolute",
                    left: 4,
                    top: 8,
                    fontSize: "1.1rem",
                    lineHeight: 1,
                    fontWeight: 900,
                    color: colors.primary.main,
                    transform: "rotate(-14deg)",
                    opacity: 0.35,
                    zIndex: 1,
                    userSelect: "none",
                  }}
                >
                  7
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    left: 14,
                    top: 10,
                    fontSize: "1.25rem",
                    lineHeight: 1,
                    fontWeight: 900,
                    color: colors.primary.main,
                    opacity: 0.6,
                    zIndex: 2,
                    userSelect: "none",
                  }}
                >
                  7
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "1.55rem",
                    lineHeight: 1,
                    fontWeight: 900,
                    color: colors.primary.main,
                    zIndex: 4,
                    userSelect: "none",
                  }}
                >
                  7
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    right: 14,
                    top: 10,
                    fontSize: "1.25rem",
                    lineHeight: 1,
                    fontWeight: 900,
                    color: colors.primary.main,
                    opacity: 0.6,
                    zIndex: 2,
                    userSelect: "none",
                  }}
                >
                  7
                </Typography>

                <Typography
                  sx={{
                    position: "absolute",
                    right: 4,
                    top: 8,
                    fontSize: "1.1rem",
                    lineHeight: 1,
                    fontWeight: 900,
                    color: colors.primary.main,
                    transform: "rotate(14deg)",
                    opacity: 0.35,
                    zIndex: 1,
                    userSelect: "none",
                  }}
                >
                  7
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  LIVE ARENA
                </Typography>

                <Typography
                  variant="h4"
                  className="slot-hero-title"
                >
                  {title}
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                maxWidth: 540,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              {description}
            </Typography>

            <Stack
              direction="row"
              gap={1.5}
              flexWrap="wrap"
              sx={{ gap: 1 }}
            >
              <CommonChip
                icon={
                  <InstantIcon sx={{ fontSize: 16 }} />
                }
                label="5-Reel Slot"
                variant="neutral"
                size="small"
              />

              <CommonChip
                icon={
                  <SparkleIcon sx={{ fontSize: 15 }} />
                }
                label="3-Tier Jackpot (Mini / Major / Mega)"
                variant="neutral"
                size="small"
              />

              <CommonChip
                icon={
                  <VerifiedIcon sx={{ fontSize: 15 }} />
                }
                label="On-Chain Results"
                variant="neutral"
                size="small"
              />
            </Stack>

            <Box sx={{ mt: 2 }}>
              <CommonButton
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => history.push(`${Path.orgcreatelottery}?type=5slot`)}
                sx={{
                  bgcolor: colors.primary.main,
                  "&:hover": { bgcolor: colors.primary.dark },
                }}
              >
                Create 5-Slot Machine
              </CommonButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              justifyContent={{ md: "flex-end" }}
            >
              <Box
                className="slot-stat-card"
                sx={{ minWidth: 140 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[500],
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Active Slots
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: colors.slate[900],
                    my: 0.25,
                  }}
                >
                  {totalActive}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: colors.emerald[600],
                    fontWeight: 600,
                  }}
                >
                  Ready to Play
                </Typography>
              </Box>

              <Box
                className="slot-stat-card"
                sx={{ minWidth: 170 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[500],
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Total Jackpot
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: colors.primary.main,
                    my: 0.25,
                  }}
                >
                  {combinedPrize}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                    }}
                  >
                    {currency}
                  </Box>
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[500],
                    fontWeight: 500,
                  }}
                >
                  Across all 5-slot pools
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FiveSlotHeroBanner;